import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { Person, Row } from 'talent_matching/types';
import {
  QuestionColumn,
  buildQuestionColumnsFromPeople,
} from 'talent_matching/components/table/question_columns';

type UseQuestionColumnsOptions = {
  rows: Row[];
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
};

type UseQuestionColumnsReturn = {
  questionColumns: QuestionColumn[];
  columnVisibilityModelWithQuestionDefaults: GridColumnVisibilityModel;
};

const useQuestionColumns = ({
  rows,
  columnVisibilityModel,
  setColumnVisibilityModel,
}: UseQuestionColumnsOptions): UseQuestionColumnsReturn => {
  const queryClient = useQueryClient();

  const questionColumns = useMemo(() => {
    const people = rows
      .map(
        (row) =>
          queryClient.getQueryData(['person', row.id]) as Person | undefined
      )
      .filter((person): person is Person => !!person);

    return buildQuestionColumnsFromPeople(people);
  }, [queryClient, rows]);

  const missingQuestionFields = useMemo(() => {
    return questionColumns
      .map((c) => c.field)
      .filter(
        (field) =>
          !Object.prototype.hasOwnProperty.call(columnVisibilityModel, field)
      );
  }, [columnVisibilityModel, questionColumns]);

  const columnVisibilityModelWithQuestionDefaults = useMemo(() => {
    if (missingQuestionFields.length === 0) {
      return columnVisibilityModel;
    }

    const missingFieldsDefaults = missingQuestionFields.reduce(
      (obj: GridColumnVisibilityModel, field) => {
        obj[field] = false;
        return obj;
      },
      {}
    );

    return {
      ...columnVisibilityModel,
      ...missingFieldsDefaults,
    };
  }, [columnVisibilityModel, missingQuestionFields]);

  useEffect(() => {
    if (missingQuestionFields.length === 0) {
      return;
    }

    setColumnVisibilityModel(columnVisibilityModelWithQuestionDefaults);
  }, [
    columnVisibilityModelWithQuestionDefaults,
    missingQuestionFields.length,
    setColumnVisibilityModel,
  ]);

  return {
    questionColumns,
    columnVisibilityModelWithQuestionDefaults,
  };
};

export default useQuestionColumns;
